import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Loader2, Newspaper, Search } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PublicNewsCard, newsExcerpt } from "@/components/PublicNewsCard";

export default function AllNews() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [disc, setDisc] = useState("tutte");
  const [visible, setVisible] = useState(9);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/news")
      .then((r) => setNews((r.data || []).filter((n) => n.published !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setVisible(9); }, [q, disc]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return news.filter((n) => {
      if (disc !== "tutte" && (n.discipline || "") !== disc) return false;
      if (!term) return true;
      return `${n.title || ""} ${newsExcerpt(n.body, 100000)}`.toLowerCase().includes(term);
    });
  }, [news, q, disc]);

  const shown = filtered.slice(0, visible);
  const DISCS = [
    { key: "tutte", label: "Tutte" },
    { key: "artistica", label: "Ginnastica Artistica" },
    { key: "ritmica", label: "Ginnastica Ritmica" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground noise-bg overflow-x-hidden">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 min-w-0">
            <SiteLogo className="w-9 h-9 shrink-0" />
            <div className="flex flex-col leading-tight min-w-0 truncate"><BrandText /></div>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5 shrink-0"><Home className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-gold border border-gold/40 rounded-full px-3 py-1"><Newspaper className="w-3 h-3" /> Notizie</span>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">Tutte le notizie</h1>
        <p className="text-muted-foreground mt-3">Tutte le novità su gare, eventi e risultati.</p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-6">
          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="news-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca una notizia…" className="pl-9" />
          </div>
          <div className="flex flex-wrap gap-2">
            {DISCS.map((d) => (
              <button key={d.key} data-testid={`news-filter-${d.key}`} onClick={() => setDisc(d.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${disc === d.key ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground border border-dashed border-white/10 rounded-xl mt-8" data-testid="news-empty">
            {news.length === 0 ? "Nessuna notizia pubblicata." : "Nessun risultato per i filtri selezionati."}
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="all-news-grid">
              {shown.map((n) => <PublicNewsCard key={n.id} item={n} />)}
            </div>
            {visible < filtered.length && (
              <div className="text-center mt-10">
                <button data-testid="load-more-btn" onClick={() => setVisible((v) => v + 9)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gold/40 text-gold font-medium hover:bg-gold hover:text-[#020617] transition-colors">
                  Mostra altre notizie ({filtered.length - visible})
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
